import { describeDomains } from "../operation-meta.js";
import { projectIDQueryArgsSchema } from "../projects-input-schemas.js";
import { NotFoundError } from "../../errors.js";
import { isStaff, scopeToReadableProjects } from "../project-access.js";
import { assertProjectWriteAccess } from "../projects-helpers.js";
export function createIntercomQueries({ defineProjectQuery, projectZql, }) {
    return {
        intercomChannels: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.projectIntercomChannels)
                    .where("projectID", args.projectID)
                    .orderBy("sortOrder", "asc");
            }),
        },
        intercomAssignments: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.projectIntercomAssignments)
                    .where("projectID", args.projectID)
                    .related("person")
                    .related("profile");
            }),
        },
        intercomProfiles: {
            byProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx, }) => {
                return scopeToReadableProjects(ctx, projectZql.projectIntercomProfiles).where("projectID", args.projectID);
            }),
        },
    };
}
/** Resolves the project of an existing intercom row and checks edit access. */
async function assertIntercomRowWriteAccess(tx, ctx, projectZql, table, id) {
    const row = await tx.run(projectZql[table].where("id", id).one());
    if (!row) {
        if (isStaff(ctx))
            return null;
        throw new NotFoundError("Intercom entry not found");
    }
    await assertProjectWriteAccess(tx, ctx, row.projectID);
    return row.projectID;
}
async function assertIntercomProfileInProject(tx, ctx, projectZql, profileID, projectID) {
    if (!profileID || isStaff(ctx))
        return;
    const profile = await tx.run(projectZql.projectIntercomProfiles.where("id", profileID).one());
    if (!profile || profile.projectID !== projectID) {
        throw new NotFoundError("Intercom profile not found");
    }
}
export function createIntercomMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createIntercomChannelInputSchema, updateIntercomChannelInputSchema, intercomChannelIDInputSchema, createIntercomAssignmentInputSchema, updateIntercomAssignmentInputSchema, intercomAssignmentIDInputSchema, createIntercomProfileInputSchema, updateIntercomProfileInputSchema, intercomProfileIDInputSchema, bulkAssignIntercomProfileInputSchema, } = schemas;
    return {
        intercomChannels: {
            create: defineProjectMutator(createIntercomChannelInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectIntercomChannels.insert({
                    id: args.id,
                    projectID: args.projectID,
                    name: args.name,
                    description: args.description ?? null,
                    channelNumber: args.channelNumber ?? null,
                    sortOrder: args.sortOrder ?? 0,
                });
            }),
            update: defineProjectMutator(updateIntercomChannelInputSchema, async ({ tx, ctx, args, }) => {
                await assertIntercomRowWriteAccess(tx, ctx, projectZql, "projectIntercomChannels", args.id);
                await tx.mutate.projectIntercomChannels.update(args);
            }),
            delete: defineProjectMutator(intercomChannelIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertIntercomRowWriteAccess(tx, ctx, projectZql, "projectIntercomChannels", args.id);
                await tx.mutate.projectIntercomChannels.delete({ id: args.id });
            }),
        },
        intercomAssignments: {
            create: defineProjectMutator(createIntercomAssignmentInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await assertIntercomProfileInProject(tx, ctx, projectZql, args.profileID, args.projectID);
                await tx.mutate.projectIntercomAssignments.insert({
                    id: args.id,
                    projectID: args.projectID,
                    personID: args.personID ?? null,
                    profileID: args.profileID ?? null,
                    unityUsername: args.unityUsername ?? null,
                    displayName: args.displayName ?? null,
                });
            }),
            update: defineProjectMutator(updateIntercomAssignmentInputSchema, async ({ tx, ctx, args, }) => {
                const projectID = await assertIntercomRowWriteAccess(tx, ctx, projectZql, "projectIntercomAssignments", args.id);
                await assertIntercomProfileInProject(tx, ctx, projectZql, args.profileID, projectID);
                await tx.mutate.projectIntercomAssignments.update(args);
            }),
            delete: defineProjectMutator(intercomAssignmentIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertIntercomRowWriteAccess(tx, ctx, projectZql, "projectIntercomAssignments", args.id);
                await tx.mutate.projectIntercomAssignments.delete({ id: args.id });
            }),
            bulkAssignProfile: defineProjectMutator(bulkAssignIntercomProfileInputSchema, async ({ tx, ctx, args, }) => {
                for (const id of args.assignmentIDs) {
                    const projectID = await assertIntercomRowWriteAccess(tx, ctx, projectZql, "projectIntercomAssignments", id);
                    await assertIntercomProfileInProject(tx, ctx, projectZql, args.profileID, projectID);
                }
                for (const id of args.assignmentIDs) {
                    await tx.mutate.projectIntercomAssignments.update({
                        id,
                        profileID: args.profileID ?? null,
                    });
                }
            }),
        },
        intercomProfiles: {
            create: defineProjectMutator(createIntercomProfileInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectIntercomProfiles.insert({
                    id: args.id,
                    projectID: args.projectID,
                    name: args.name,
                    channelConfig: args.channelConfig ?? {},
                });
            }),
            update: defineProjectMutator(updateIntercomProfileInputSchema, async ({ tx, ctx, args, }) => {
                await assertIntercomRowWriteAccess(tx, ctx, projectZql, "projectIntercomProfiles", args.id);
                await tx.mutate.projectIntercomProfiles.update(args);
            }),
            delete: defineProjectMutator(intercomProfileIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertIntercomRowWriteAccess(tx, ctx, projectZql, "projectIntercomProfiles", args.id);
                await tx.mutate.projectIntercomProfiles.delete({ id: args.id });
            }),
        },
    };
}
export const intercomDomainDescriptions = describeDomains({
    intercomChannels: "Intercom channels of a project",
    intercomAssignments: "Which person uses which intercom profile/channels on a project",
    intercomProfiles: "Intercom key/channel profiles of a project",
});
